/**
 * Bundle 58 - Profile Gate photo tile with defensive load / error
 * fallback. Extracted from ProfileCompletionGate.jsx so it can be
 * unit-tested in isolation without pulling in react-router.
 *
 * Root cause of the "blank gray square" bug: BE returned photo URLs
 * prefixed with `PUBLIC_BASE_URL` (production host) even from the
 * preview backend, so the browser fetched a URL that never existed
 * on prod and silently 404'd. The tile happily rendered an `<img>`
 * with a broken src and no fallback. Bundle 58 makes BE return
 * relative URLs; this tile guards against any future URL breakage
 * with explicit load / error state.
 */
import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';

export const PhotoTile = ({ photo, onRemove }) => {
  const src = photo?.thumb_url || photo?.url || '';
  const [state, setState] = useState(src ? 'loading' : 'errored');

  return (
    <div
      className="relative aspect-square rounded-xl overflow-hidden border border-neutral-200 bg-neutral-100"
      data-testid={`profile-gate-photo-tile-${photo?.id || 'unknown'}`}
    >
      {src && state !== 'errored' && (
        <img
          src={src}
          alt="Dostination profile"
          className={`w-full h-full object-cover ${state === 'loaded' ? '' : 'opacity-0'}`}
          onLoad={() => setState('loaded')}
          onError={() => setState('errored')}
          data-testid="profile-gate-photo-tile-img"
        />
      )}
      {state === 'loading' && (
        <div
          className="absolute inset-0 flex items-center justify-center text-neutral-400"
          data-testid="profile-gate-photo-tile-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
      {state === 'errored' && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2 text-center text-[10px] text-accent-800 bg-accent-50"
          data-testid="profile-gate-photo-tile-error"
        >
          <span className="font-semibold">Preview unavailable</span>
          <span className="opacity-70">Upload saved. Try re-adding.</span>
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow-sm"
        aria-label="Remove"
        data-testid={`profile-gate-photo-remove-${photo?.id || 'unknown'}`}
      ><X className="h-3 w-3 text-accent-700" /></button>
    </div>
  );
};

export default PhotoTile;
